import { type PrescriptionPrescriptionTypeEnum } from '@redteclab/api/clients/bully'

import { gtmBuildGtmApiUrl } from '../../gtm'
import { type TENANT_AND_ENV, TENANT_AND_ENV_TO_HOST } from '../../tenant'
import { queryStringEncode } from '../../url-handling'
import {
  type CartIntermediateEntryObject,
  type GtmDataLayerCartIntermediateUrlDataType,
} from '../model/CartIntermediateContextValue'

type GetEntriesdataResponseType = {
  offers: string[]
  prescriptionType: PrescriptionPrescriptionTypeEnum
  products: string[]
}

const getEntriesData = ({
  entries,
}: {
  entries: CartIntermediateEntryObject[]
}): GetEntriesdataResponseType => {
  const [firstEntry] = entries
  const { prescriptionType } = firstEntry

  const offers = entries.map((entry) => entry.id)
  const products = entries.flatMap(({ product }) => {
    if (!product) {
      return []
    }

    return ['id' in product ? product.id : product.upid]
  })

  return { offers, prescriptionType, products }
}

/**
 * We are currently using the same data layer structure as the old cart intermediate page.
 * To ensure consistency, we need to build it statically—just like we did in the old cart intermediate page in Fock (as discussed with Damian Dudek).
 * Since this new cart intermediate page is replacing the old one, maintaining the same data layer format is important for testing purposes.
 * This implementation is temporary and will be refactored once the dependent services are updated.
 *
 * @returns:
 * - cartIntermediateRequestUrl: the final URL with products data to be added to initial dataLayer
 * - cartIntermediateGtmDataApiUrl: the URL to be used to fetch gtm data from the /cartintermediate endpoint
 */
export const gtmDataLayerGetCartIntermediateUrlData = ({
  entries,
  language,
  quantity,
  tenantAndEnv,
  zipCode,
}: {
  entries: CartIntermediateEntryObject[] | null
  language: string
  quantity: number
  tenantAndEnv: TENANT_AND_ENV
  zipCode?: string
}): GtmDataLayerCartIntermediateUrlDataType | null => {
  if (!entries) {
    return null
  }

  const entriesData = getEntriesData({ entries })
  const { offers, prescriptionType, products } = entriesData
  const shopUrl = TENANT_AND_ENV_TO_HOST[tenantAndEnv]
  const queryParameters = {
    cartAction: 'display',
    channel: 'direct',
    eventName: 'addToCart_PDP',
    eventType: 'conversion',
    offer: offers,
    prescriptionType,
    product: products,
    quantity: String(quantity),
    zipCode: zipCode ?? '',
  }

  // this URL will be added to initial dataLayer
  const cartIntermediateRequestUrl = `${shopUrl}/${tenantAndEnv}/${language}/cartintermediate/${queryStringEncode(queryParameters)}`

  // this URL will be used to fetch gtm data from the /cartintermediate endpoint
  const cartIntermediateGtmDataApiUrl = gtmBuildGtmApiUrl({
    language,
    pageType: 'cartintermediate',
    queryParameters: { ...queryParameters, upid: products },
    tenantAndEnv,
  })

  return {
    cartIntermediateGtmDataApiUrl,
    cartIntermediateRequestUrl,
  }
}
